import React, { useEffect, useState } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { Link, useNavigate } from 'react-router-dom';
import { fetchScenarios, createScenario, deleteScenario, updateScenario } from '../slices/scenarioSlice';
import {
  HiOutlinePlus, HiOutlineTrash, HiOutlinePencil, HiOutlineLightningBolt,
  HiOutlineDotsVertical, HiOutlinePlay, HiOutlinePause, HiOutlineClipboardCopy,
} from 'react-icons/hi';




export default function Scenarios() {
  const dispatch = useDispatch();
  const navigate = useNavigate();
  const { list, loading } = useSelector((s) => s.scenarios);
  const [showCreate, setShowCreate] = useState(false);
  const [newName, setNewName] = useState('');

  useEffect(() => {
    dispatch(fetchScenarios());
  }, [dispatch]);

  const handleCreate = (e) => {
    e.preventDefault();
    if (!newName.trim()) return;
    dispatch(createScenario({ name: newName.trim() })).then((action) => {
      if (action.payload?._id) {
        navigate(`/scenarios/${action.payload._id}`);
      }
    });
    setNewName('');
    setShowCreate(false);
  };

  const handleDelete = (id) => {
    if (window.confirm('Delete this scenario?')) {
      dispatch(deleteScenario(id));
    }
  };

  const toggleStatus = (scenario) => {
    const newStatus = scenario.status === 'active' ? 'inactive' : 'active';
    dispatch(updateScenario({ id: scenario._id, data: { status: newStatus } }));
  };

  if (loading && !list.length) {
    return (
      <div className="flex items-center justify-center h-64">
        <div className="w-8 h-8 border-4 border-brand-500 border-t-transparent rounded-full animate-spin" />
      </div>
    );
  }

  return (
    <div className="animate-fade-in max-w-5xl space-y-6">
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-2xl font-bold">Scenarios</h1>
          <p className="text-gray-500 dark:text-gray-400 text-sm mt-1">Manage your automation workflows</p>
        </div>
        <button
          onClick={() => setShowCreate(true)}
          className="flex items-center gap-2 px-4 py-2.5 bg-brand-600 hover:bg-brand-700 text-white rounded-lg text-sm font-medium transition"
        >
          <HiOutlinePlus size={18} /> New Scenario
        </button>
      </div>

      {/* Create Modal */}
      {showCreate && (
        <div className="fixed inset-0 bg-black/40 flex items-center justify-center z-50" onClick={() => setShowCreate(false)}>
          <div className="bg-white dark:bg-gray-900 rounded-2xl p-6 w-full max-w-md shadow-xl border border-gray-200 dark:border-gray-800" onClick={(e) => e.stopPropagation()}>
            <h3 className="text-lg font-semibold mb-4">Create Scenario</h3>
            <form onSubmit={handleCreate}>
              <input
                autoFocus value={newName} onChange={(e) => setNewName(e.target.value)}
                placeholder="Scenario name"
                className="w-full px-4 py-2.5 rounded-lg border border-gray-300 dark:border-gray-700 bg-white dark:bg-gray-800 focus:ring-2 focus:ring-brand-500 focus:border-transparent outline-none mb-4"
              />
              <div className="flex gap-3 justify-end">
                <button type="button" onClick={() => setShowCreate(false)} className="px-4 py-2 rounded-lg text-sm border border-gray-300 dark:border-gray-700 hover:bg-gray-50 dark:hover:bg-gray-800 transition">
                  Cancel
                </button>
                <button type="submit" className="px-4 py-2 rounded-lg text-sm bg-brand-600 text-white hover:bg-brand-700 transition font-medium">
                  Create
                </button>
              </div>
            </form>
          </div>
        </div>
      )}

      {/* Scenario List */}
      {list.length === 0 ? (
        <div className="text-center py-16 bg-white dark:bg-gray-900 rounded-xl border border-gray-200 dark:border-gray-800">
          <HiOutlineLightningBolt size={40} className="mx-auto mb-3 text-gray-300 dark:text-gray-600" />
          <p className="text-gray-500 dark:text-gray-400">No scenarios yet. Create your first automation.</p>
        </div>
      ) : (
        <div className="grid gap-3">
          {list.map((scenario) => (
            <div key={scenario._id} className="bg-white dark:bg-gray-900 rounded-xl border border-gray-200 dark:border-gray-800 p-5 flex items-center gap-4 hover:border-brand-300 dark:hover:border-brand-700 transition-colors group">
              <div className="flex-1 min-w-0">
                <Link to={`/scenarios/${scenario._id}`} className="font-semibold text-base hover:text-brand-600 transition-colors">
                  {scenario.name}
                </Link>
                <div className="flex items-center gap-3 mt-1.5 text-xs text-gray-500 dark:text-gray-400">
                  <span>{scenario.nodes?.length || 0} nodes</span>
                  <span>·</span>
                  <span>{new Date(scenario.createdAt).toLocaleDateString()}</span>
                </div>
              </div>

              <button
                onClick={() => toggleStatus(scenario)}
                className={`px-3 py-1.5 rounded-full text-xs font-semibold transition ${
                  scenario.status === 'active'
                    ? 'bg-green-100 dark:bg-green-900/30 text-green-700 dark:text-green-400'
                    : 'bg-gray-100 dark:bg-gray-800 text-gray-500 dark:text-gray-400'
                }`}
              >
                {scenario.status === 'active' ? 'Active' : 'Inactive'}
              </button>

              <Link to={`/scenarios/${scenario._id}`} className="p-2 rounded-lg hover:bg-gray-100 dark:hover:bg-gray-800 text-gray-400 transition">
                <HiOutlinePencil size={18} />
              </Link>

              <button onClick={() => handleDelete(scenario._id)} className="p-2 rounded-lg hover:bg-red-50 dark:hover:bg-red-900/20 text-gray-400 hover:text-red-500 transition">
                <HiOutlineTrash size={18} />
              </button>
            </div>
          ))}
        </div>
      )}
    </div>
  );
}
